// Aba Monitor do painel Sistema — saúde do servidor twms-ia (host/GPU/stack/serviços)
// + toggles admin + backup. Extraído do antigo TecnologiaPanel. Consome o estado de
// useSystemMetrics (passado por prop, compartilhado com a aba Arquitetura).
import type { ReactNode } from 'react'
import {
  FiCpu, FiActivity, FiHardDrive, FiServer, FiRefreshCw, FiPause, FiPlay, FiZap,
} from 'react-icons/fi'
import { AreaChart, Area, ResponsiveContainer } from 'recharts'
import { fmtBytes, fmtUptime, dotColor } from '../hooks/useSystemMetrics'
import type { UseSystemMetrics, SvcHealth } from '../hooks/useSystemMetrics'
import { LEVEL_TEXT, LEVEL_BAR, usageLevel, CHART_COLORS } from '../lib/chartTheme'

// Cor por nível de uso: verde < 60, âmbar < 85, vermelho ≥ 85.
// A escala (60 avisa / 85 alerta) vive no chartTheme — estava duplicada
// literalmente aqui e no SistemaPanel.
function usageText(p: number | null | undefined): string { return LEVEL_TEXT[usageLevel(p)] }
function usageBar(p: number | null | undefined): string { return LEVEL_BAR[usageLevel(p)] }

function Bar({ pct }: { pct: number | null | undefined }) {
  const w = Math.max(0, Math.min(100, pct ?? 0))
  return (
    <div className="h-1.5 w-full rounded-full bg-muted/70 overflow-hidden neu-gauge">
      <div className={`h-full rounded-full transition-all duration-500 ${usageBar(pct)}`} style={{ width: `${w}%` }} />
    </div>
  )
}

function Spark({ data, color }: { data: number[]; color: string }) {
  const d = data.map((v, i) => ({ i, v }))
  return (
    <div className="h-5 w-full">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={d} margin={{ top: 1, right: 0, bottom: 0, left: 0 }}>
          <Area type="monotone" dataKey="v" stroke={color} fill={color} fillOpacity={0.15} strokeWidth={1.25} isAnimationActive={false} dot={false} />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  )
}

// Agrupamento dos containers por stack. Com 31 containers no host (3 produções,
// 3 devs, IA, infra) a lista plana virou ilegível — o grupo sai do nome, que é o
// único dado que o exporter entrega.
const GROUPS: [string, RegExp][] = [
  ['Produção', /^(crm-twms|fin-twms|cobranca-externa)-/],
  ['Dev', /^(crm-dev|fin-dev|cob-dev)-/],
  ['IA e voz', /^(moss|whisper|ollama|omnivoice|open-webui)/],
  ['Infra', /^(traefik|gitea|portainer|nominatim|obsidian|onlyoffice)/],
]
const groupOf = (n: string) => GROUPS.find(([, re]) => re.test(n))?.[0] || 'Outros'
// Nome curto SEM perder de qual app é: dentro de Produção/Dev, tirar o prefixo
// inteiro deixaria nove "backend/frontend/mysql" indistinguíveis. O ambiente já
// é dito pelo título do grupo, então sobra só o app.
const APP_OF: [RegExp, string][] = [
  [/^(crm-twms|crm-dev)-/, 'crm'],
  [/^(fin-twms|fin-dev)-/, 'fin'],
  [/^(cobranca-externa|cob-dev)-/, 'cob'],
]
function shortName(n: string): string {
  for (const [re, app] of APP_OF) if (re.test(n)) return `${app} · ${n.replace(re, '')}`
  return n
}
// Parado de propósito (o MySQL da pasta dele segue servindo o moss-v3): não conta
// como falha nem entra no denominador de "up", senão o painel acusa problema eterno.
const FROZEN = new Set(['moss-v2'])

function Tile({ icon, label, value, valueClass, pct, spark, sparkColor, sub }: {
  icon: ReactNode; label: string; value: string; valueClass?: string
  pct?: number | null; spark?: number[]; sparkColor?: string; sub?: string
}) {
  return (
    <div className="glass-panel neu-card rounded-2xl p-4 flex flex-col gap-2.5 transition-shadow hover:shadow-lg">
      <span className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{icon}{label}</span>
      <span className={`text-3xl font-semibold leading-none tabular-nums ${valueClass || 'text-foreground'}`}>{value}</span>
      {pct != null && <Bar pct={pct} />}
      {spark && spark.length > 1 && <Spark data={spark} color={sparkColor || CHART_COLORS[0]} />}
      {sub && <span className="text-[11px] text-muted-foreground truncate">{sub}</span>}
    </div>
  )
}

export function SystemMonitor({ m, embedded = false }: { m: UseSystemMetrics; embedded?: boolean }) {
  const { metrics, error, forbidden, paused, setPaused, lastUpdate, cpuHist, memHist, gpuHist, fetchMetrics } = m

  if (forbidden) {
    return (
      <div className="h-full flex items-center justify-center text-muted-foreground">
        <div className="text-center"><FiServer className="w-7 h-7 mx-auto mb-2 opacity-40" /><p className="text-sm">Painel restrito a administradores.</p></div>
      </div>
    )
  }

  const host = metrics?.host
  const svc = metrics?.services || {}
  const exporterDown = svc.exporter?.status && svc.exporter.status !== 'healthy'
  const dgpu = metrics ? metrics.gpu.slice().sort((a, b) => b.vram_total_b - a.vram_total_b)[0] : undefined
  const liveDocker = metrics ? metrics.docker.filter(d => !FROZEN.has(d.name)) : []
  const upCount = liveDocker.filter(d => d.state === 'running').length
  const liveTotal = liveDocker.length
  // Ordem dos grupos = ordem de GROUPS ('Outros' por último); dentro do grupo, alfabética.
  const order = [...GROUPS.map(([g]) => g), 'Outros']
  const groupedDocker = order
    .map(g => [g, (metrics?.docker || []).filter(d => groupOf(d.name) === g).sort((a, b) => a.name.localeCompare(b.name))] as const)
    .filter(([, list]) => list.length > 0)
  const services: [string, SvcHealth | undefined, string?][] = [
    ['MySQL', svc.mysql, typeof svc.mysql?.latency_ms === 'number' ? `${svc.mysql.latency_ms}ms` : undefined],
    ['Oracle', svc.oracle, typeof svc.oracle?.poolInUse === 'number' ? `${svc.oracle.poolInUse}/${svc.oracle.poolOpen}` : undefined],
    ['Sidecar', svc.sidecar, undefined],
    ['Exporter', svc.exporter, undefined],
  ]

  const content = (
    // h-full + min-h-0: o bloco ocupa a altura que recebe e NÃO cresce além dela.
    // Quem rola é a lista de containers dentro do próprio card, não o painel.
    <div className={embedded ? 'flex flex-col gap-4 h-full min-h-0' : 'w-full max-w-[1600px] mx-auto flex flex-col gap-4'}>
        {/* Título + controles */}
        <div className="flex items-center justify-between gap-2 shrink-0">
          <div className="flex items-center gap-2">
            <FiActivity className="w-5 h-5 text-primary" />
            <h2 className="text-base font-bold text-foreground">Monitor do servidor</h2>
            {host && <span className="text-xs text-muted-foreground">{host.hostname} · {fmtUptime(host.uptime_s)}</span>}
          </div>
          <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
            {lastUpdate && <span className="hidden sm:inline tabular-nums">{lastUpdate.toLocaleTimeString('pt-BR')}</span>}
            <button onClick={() => fetchMetrics()} title="Atualizar" className="p-1.5 rounded-lg hover:bg-muted transition-colors"><FiRefreshCw className="w-3.5 h-3.5" /></button>
            <button onClick={() => setPaused(p => !p)} title={paused ? 'Retomar' : 'Pausar'}
              className={`p-1.5 rounded-lg transition-colors ${paused ? 'bg-amber-500/15 text-amber-600 dark:text-amber-400' : 'hover:bg-muted'}`}>
              {paused ? <FiPlay className="w-3.5 h-3.5" /> : <FiPause className="w-3.5 h-3.5" />}
            </button>
          </div>
        </div>

        {error && !metrics && (
          <div className="px-3 py-2 rounded-xl neu-tag bg-red-500/10 border border-red-500/30 text-xs text-red-700 dark:text-red-300">
            Falha ao carregar métricas: {error}
          </div>
        )}
        {exporterDown && (
          <div className="px-3 py-2 rounded-xl neu-tag bg-amber-500/10 border border-amber-500/30 text-xs text-amber-700 dark:text-amber-300">
            Exporter do host inacessível — métricas de máquina/GPU/stack off. Saúde do app abaixo segue.
          </div>
        )}

        {/* 4 tiles full-width: CPU / RAM / Disco / GPU */}
        {host && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 shrink-0">
            <Tile icon={<FiCpu className="w-3.5 h-3.5" />} label="CPU" value={`${host.cpu.usage_pct ?? '—'}%`}
              valueClass={usageText(host.cpu.usage_pct)} pct={host.cpu.usage_pct} spark={cpuHist} sparkColor="#10b981"
              sub={`${host.cpu.cores}c · load ${host.cpu.load[0]?.toFixed(2)}`} />
            <Tile icon={<FiActivity className="w-3.5 h-3.5" />} label="RAM" value={`${host.mem.usage_pct}%`}
              valueClass={usageText(host.mem.usage_pct)} pct={host.mem.usage_pct} spark={memHist} sparkColor="#3b82f6"
              sub={`${fmtBytes(host.mem.used_b)}/${fmtBytes(host.mem.total_b)}`} />
            <Tile icon={<FiHardDrive className="w-3.5 h-3.5" />} label="Disco" value={`${host.disk[0]?.usage_pct ?? '—'}%`}
              valueClass={usageText(host.disk[0]?.usage_pct)} pct={host.disk[0]?.usage_pct}
              sub={host.disk[0] ? `${fmtBytes(host.disk[0].used_b)}/${fmtBytes(host.disk[0].total_b)}` : undefined} />
            <Tile icon={<FiZap className="w-3.5 h-3.5" />} label="GPU" value={dgpu ? `${dgpu.busy_pct}%` : '—'}
              valueClass={usageText(dgpu?.busy_pct)} pct={dgpu?.busy_pct} spark={gpuHist} sparkColor="#8b5cf6"
              sub={dgpu ? `${fmtBytes(dgpu.vram_used_b)}/${fmtBytes(dgpu.vram_total_b)} · ${dgpu.temp_c ?? '—'}°` : undefined} />
          </div>
        )}

        {/* Stack · Serviços — lado a lado em telas largas. Recebe a altura que
            sobrou e a lista rola DENTRO do card. O min-h é um piso: em tela
            baixa o bloco para de encolher e quem rola volta a ser o painel —
            uma janela de 50px pra 31 containers é pior que rolagem de página. */}
        <div className={`grid grid-cols-1 lg:grid-cols-2 gap-4 ${embedded ? 'flex-1 min-h-[280px]' : ''}`}>
          {metrics && metrics.docker.length > 0 && (
            <div className="glass-panel neu-card rounded-2xl p-5 flex flex-col gap-3 min-h-0">
              <div className="flex items-center justify-between shrink-0">
                <span className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  <FiServer className="w-3.5 h-3.5" /> Stack Docker
                </span>
                <span className="text-[11px] font-semibold tabular-nums px-2 py-0.5 rounded-full bg-emerald-500/12 text-emerald-600 dark:text-emerald-400"
                  title="Containers rodando, ignorando os parados de propósito">{upCount}/{liveTotal} up</span>
              </div>
              <div className="flex flex-col gap-3 min-h-0 overflow-y-auto pr-1">
                {groupedDocker.map(([group, list]) => {
                  const live = list.filter(d => !FROZEN.has(d.name))
                  const up = live.filter(d => d.state === 'running').length
                  return (
                    <div key={group} className="flex flex-col gap-1.5">
                      <div className="flex items-baseline gap-2">
                        <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground/80">{group}</span>
                        <span className={`text-[10px] tabular-nums ${up === live.length ? 'text-muted-foreground/60' : 'text-amber-600 dark:text-amber-400'}`}>
                          {up}/{live.length}
                        </span>
                        <span className="flex-1 border-b border-border/40 translate-y-[-2px]" />
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-5 gap-y-2">
                        {list.map(d => {
                          const frozen = FROZEN.has(d.name)
                          const badge = d.health || (d.state === 'running' ? 'healthy' : 'unhealthy')
                          return (
                            <div key={d.name} className="flex items-center gap-2 min-w-0"
                              title={frozen ? `${d.name}: congelado de propósito (o MySQL da pasta segue servindo o moss-v3)`
                                            : `${d.status} · cpu ${d.cpu_pct ?? '—'}% · mem ${fmtBytes(d.mem_used_b)} · up ${fmtUptime(d.uptime_s)}`}>
                              <span className={`w-2 h-2 rounded-full shrink-0 ${frozen ? 'bg-slate-400' : dotColor(badge)}`} />
                              <span className={`text-[12px] truncate ${frozen ? 'text-muted-foreground' : 'text-foreground'}`}>
                                {shortName(d.name)}
                              </span>
                              {frozen && <span className="text-[9px] text-muted-foreground shrink-0">congelado</span>}
                              {!frozen && !!d.restarts && <span className="text-[9px] text-amber-600 dark:text-amber-400 shrink-0" title={`${d.restarts} reinício(s)`}>↻{d.restarts}</span>}
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          )}

          {metrics && (
            <div className="glass-panel neu-card rounded-2xl p-5 flex flex-col gap-3 min-h-0">
              <span className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground shrink-0">
                <FiActivity className="w-3.5 h-3.5" /> Serviços
              </span>
              <div className="grid grid-cols-2 gap-x-5 gap-y-3 min-h-0 overflow-y-auto pr-1 content-start">
                {services.map(([label, s, detail]) => (
                  <div key={label} className="flex items-center gap-2 min-w-0">
                    <span className={`w-2 h-2 rounded-full shrink-0 ${dotColor(s?.status)}`} />
                    <span className="text-[12px] text-foreground truncate">{label}</span>
                    {detail && <span className="text-[10px] text-muted-foreground shrink-0 ml-auto tabular-nums">{detail}</span>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
  )
  return embedded ? content : <div className="h-full overflow-y-auto p-4">{content}</div>
}
